import { useRequestMembershipMutation } from '#app/__generated__/relay/useRequestMembershipMutation.graphql.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useCurrentRouteKey } from '#app/hooks/useCurrentRouteKey.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { logger } from '#app/services/logger.js';
import { prettifyRouteKey } from '#app/utilities/prettifyRouteKey.js';
import { Alpha2CountryCodes } from '@contra/graphql-types';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useMemo } from 'react';

const log = logger.child({ namespace: 'useRequestCompliantContract' });

type HandleRequestMembershipArgs = {
  companyName: string;
  contractorHiresPerQuarter?: number;
  joinMembershipToday?: boolean;
  message: string;
  name: string;
  phoneNumber: string;
  workspaceId: string;
};

type UseRequestMembershipPayload = {
  fireRequestMembership: () => void;
  handleRequestMembership: (args: HandleRequestMembershipArgs) => void;
  hasRequestedMembership: boolean;
  isRequesting: boolean;
};

type UseRequestMembershipProps = {
  customTitle?: string;
  parentAnalyticsSection: string;
  prefilledCompanyName?: null | string;
  prefilledFullName?: string;
  prefilledPhoneNumber?: string;
  prefilledPhoneNumberCountryCode?: Alpha2CountryCodes;
  prefilledPhoneNumberDialingCode?: string;
  redirectTo?: string;
  source?: string;
};

export const useRequestMembership = ({
  customTitle,
  parentAnalyticsSection,
  prefilledCompanyName,
  prefilledFullName,
  prefilledPhoneNumber,
  prefilledPhoneNumberCountryCode,
  prefilledPhoneNumberDialingCode,
  redirectTo,
  source,
}: UseRequestMembershipProps): UseRequestMembershipPayload => {
  const modalSource = source ?? 'client_upgrade'; // client_upgrade is the name of the button in the navbar
  const currentRouteKey = useCurrentRouteKey();

  const toast = useToast();
  const currentUser = useCurrentUser();
  const location = useLocation();

  const [requestMembership, isRequesting] =
    useContraMutation<useRequestMembershipMutation>(graphql`
      mutation useRequestMembershipMutation($input: RequestMembershipInput!) {
        requestMembership(input: $input) {
          errors {
            __typename
            message
          }
          visitor {
            userAccount {
              ...CurrentUser
            }
          }
        }
      }
    `);

  const handleRequestMembership = useCallback(
    ({
      companyName,
      contractorHiresPerQuarter,
      joinMembershipToday,
      message,
      name,
      phoneNumber,
      workspaceId,
    }: HandleRequestMembershipArgs) => {
      if (!currentUser && !workspaceId) {
        return;
      }

      requestMembership({
        onCompleted: (errors) => {
          const { apiErrors, payloadErrors } = errors;

          if (payloadErrors) {
            for (const payloadError of payloadErrors) {
              const errorContext = {
                locations: payloadError.locations,
                severity: payloadError.severity,
              };
              captureException(payloadError.message, {
                extra: errorContext,
              });
              log.error(errorContext, payloadError.message);
              toast.error(
                'An unexpected error occurred. Our team has been notified.',
              );
            }

            return;
          }

          if (apiErrors) {
            for (const apiError of apiErrors) {
              toast.error(apiError.message);
              log.debug(apiError, apiError.message);
            }

            return;
          }

          toast.success('Membership requested!');
        },
        onError: (error) => {
          captureException(error);
          toast.error(
            'Oops! Something went wrong. Our team has been notified. Please try again.',
          );
        },
        variables: {
          input: {
            companyName,
            contractorHiresPerQuarter,
            joinMembershipToday: joinMembershipToday ?? false,
            message,
            name,
            phoneNumber,
            requestedFromUrl: location.currentURL,
            routeName: prettifyRouteKey(currentRouteKey),
            triggerSection: modalSource,
            workspaceId:
              currentUser?.workspaceForSelectedUserType.id ?? workspaceId,
          },
        },
      });
    },
    [
      currentRouteKey,
      currentUser,
      location.currentURL,
      modalSource,
      requestMembership,
      toast,
    ],
  );

  const { openModal: openRequestMembershipModal } = useModal({
    identifier: 'RequestMembershipModal',
    modalProps: {
      companyName: prefilledCompanyName ?? undefined,
      customTitle,
      fullName: prefilledFullName ?? undefined,
      isSubmitting: isRequesting,
      onSubmit: (data) => {
        handleRequestMembership(data);
      },
      parentAnalyticsSection,
      phoneNumber: prefilledPhoneNumber ?? undefined,
      phoneNumberCountryCode: prefilledPhoneNumberCountryCode ?? undefined,
      phoneNumberDialingCode: prefilledPhoneNumberDialingCode ?? undefined,
      redirectTo,
      source: modalSource ?? undefined,
    },
    staticAdditionalTrackingProperties: {
      section: parentAnalyticsSection,
    },
  });

  const fireRequestMembership = useCallback(() => {
    if (
      currentUser?.workspaceForSelectedUserType.clientMembershipRequest
        ?.hasRequestedMembership
    ) {
      return;
    }

    openRequestMembershipModal();
  }, [currentUser, openRequestMembershipModal]);

  const hasRequestedMembership = useMemo(
    () =>
      Boolean(
        currentUser?.workspaceForSelectedUserType.clientMembershipRequest
          ?.hasRequestedMembership,
      ),
    [currentUser],
  );

  return {
    fireRequestMembership,
    handleRequestMembership,
    hasRequestedMembership,
    isRequesting,
  };
};
